import {FeatureToggle, isFeatureEnabled} from '@/helpers/featureToggle'
import {useJourneyMetaConfig} from '@/hooks/useJourneyMetaConfig'
import {showNotification} from '@ReduxActions'
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
  GetJourneysFilters,
  GetJourneysUseCaseParams,
  getJourneysUseCase,
} from '@UseCases'
import {keepPreviousData, useQuery, useQueryClient} from '@tanstack/react-query'
import type {Table} from '@tanstack/react-table'
import {DataTable} from 'pepsico-ds'
import {
  ComponentPropsWithRef,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {store} from '../../store'
import type {JourneyListItem} from '../../types/Journeys'
import {getTableColumns} from './components/columns'

const initialRequestParams = {
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  sort: DEFAULT_SORT,
}

type JourneysListTableProps = ComponentPropsWithRef<'div'>

export type JourneysListTableHandler = {
  reload: () => void
}

export const JourneysListTable = forwardRef<JourneysListTableHandler, JourneysListTableProps>(
  (_, ref: Ref<unknown>) => {
    const {formatMessage} = useIntl()
    const [searchParams, setSearchParams] = useSearchParams()
    const pageInSearchParams = searchParams.get('page')
    const sortInSearchParams = searchParams.get('sort')
    const sizeInSearchParams = searchParams.get('size')
    const {country} = useJourneyMetaConfig()

    const queryClient = useQueryClient()
    const {requestParams, setRequestParams} = useRequestParams(
      pageInSearchParams,
      sizeInSearchParams,
      sortInSearchParams
    )
    const onPageIndexChange = useOnPageIndexChange(requestParams.page, setRequestParams, setSearchParams)
    const onRowsPerPageChange = useOnRowsPerPageChange(requestParams.size, setRequestParams, setSearchParams)
    const onSortingDidChange = useOnSortingDidChange(requestParams.sort, setRequestParams, setSearchParams)

    const [dateFilters, setDateFilters] = useState<GetJourneysFilters>({})

    const resetToFirstPage = useCallback(() => {
      setRequestParams(prev => ({...prev, page: 0}))
      setSearchParams(oldParams => {
        oldParams.set('page', '1')
        return oldParams
      })
    }, [setRequestParams, setSearchParams])

    const onColumnFiltersChange = useCallback(
      (value: unknown, type: 'text' | 'dropdown' | 'date' | 'dateRange' | undefined, columnKey: string) => {
        const directKeyMap: Partial<Record<string, keyof GetJourneysFilters>> = {
          status: 'status',
          type: 'type',
          name: 'name',
          publishedBy: 'publishedBy',
        }
        const directKey = directKeyMap[columnKey]
        if (directKey && (type === 'dropdown' || type === 'text')) {
          const filterValue =
            type === 'dropdown'
              ? ((value as {id: string}[] | undefined) ?? [])[0]?.id
              : // pepsico-ds debounces text filters; value is the typed string
                (value as string | undefined)?.trim() || undefined
          setDateFilters(prev => ({...prev, [directKey]: filterValue}))
          resetToFirstPage()
          return
        }

        if (type !== 'dateRange') return
        const range = value as {from?: Date; to?: Date} | undefined
        const fromISO = range?.from
          ? (() => {
              const d = new Date(range.from)
              d.setHours(0, 0, 0, 0)
              return d.toISOString()
            })()
          : undefined
        const toISO = range?.to
          ? (() => {
              const d = new Date(range.to)
              d.setHours(23, 59, 59, 999)
              return d.toISOString()
            })()
          : undefined
        const dateRangeKeyMap: Record<
          string,
          keyof Pick<
            GetJourneysFilters,
            | 'createdOnStart'
            | 'createdOnEnd'
            | 'modifiedOnStart'
            | 'modifiedOnEnd'
            | 'publishedOnStart'
            | 'publishedOnEnd'
          >
        > = {
          createdAt: 'createdOnStart',
          updatedAt: 'modifiedOnStart',
          publishedAt: 'publishedOnStart',
        }
        const fromKey = dateRangeKeyMap[columnKey]
        const toKey = fromKey?.replace('Start', 'End') as keyof GetJourneysFilters | undefined
        if (!fromKey || !toKey) return
        setDateFilters(prev => ({...prev, [fromKey]: fromISO, [toKey]: toISO}))
        resetToFirstPage()
      },
      [resetToFirstPage]
    )

    const onTableReady = useCallback(
      (table: Table<JourneyListItem>) => {
        const [id, dir] = requestParams.sort.split(',')
        if (id) table.setSorting([{id, desc: dir === 'desc'}])
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [] // sets the column header sort indicator once on mount to match the initial API sort
    )

    const {isFetching, data, error} = useQuery({
      queryKey: ['journeys', requestParams, country, dateFilters],
      queryFn: ({signal}) => getJourneysUseCase({...requestParams, ...dateFilters}, signal),
      placeholderData: keepPreviousData,
    })

    // true only on the first load before any data arrives; re-fetches keep data via keepPreviousData
    const isInitialLoading = isFetching && !data

    useEffect(() => {
      if (error) {
        onLoadJourneysError()
      }
    }, [error])

    useImperativeHandle(ref, () => ({
      reload: () => {
        setRequestParams(prev => ({
          ...prev,
          page: pageInSearchParams ? initialRequestParams.page : prev.page,
          size: sizeInSearchParams ? initialRequestParams.size : prev.size,
          sort: sortInSearchParams?.length ? initialRequestParams.sort : prev.sort,
        }))
        setSearchParams(oldParams => {
          oldParams.set('page', '1')
          return oldParams
        })
        queryClient.invalidateQueries({queryKey: ['journeys', requestParams]})
      },
    }))

    const columns = useMemo(
      () =>
        getTableColumns(formatMessage).filter(col => {
          if (col.columnKey === 'country') return isFeatureEnabled(FeatureToggle.userRole)
          if (col.columnKey === 'description') return !isFeatureEnabled(FeatureToggle.journeyType)
          if (col.columnKey === 'type') return isFeatureEnabled(FeatureToggle.journeyType)
          return true
        }),
      [formatMessage]
    )

    return (
      <>
        {isInitialLoading && (
          <p className="text-md my-16 items-center text-center" data-testid="journeys-loading">
            {formatMessage({id: 'JOURNEY_LIST.LOADING_TABLE_LABEL'})}
          </p>
        )}
        {!isInitialLoading && error && (
          <p className="text-md my-16 items-center text-center" data-testid="journeys-error">
            {formatMessage({id: 'JOURNEY_LIST.ERROR_TABLE_LABEL'})}
          </p>
        )}
        {!isInitialLoading && !error && (
          <div data-testid="journeys-content">
            <DataTable
              columns={columns}
              data={data?.content ?? []}
              isLoading={isFetching}
              manualPagination
              pageIndex={requestParams.page}
              pageSize={requestParams.size}
              totalRowsCount={data?.pagination.totalElements ?? 0}
              onPageIndexChange={onPageIndexChange}
              onRowsPerPageChange={onRowsPerPageChange}
              enableSorting
              manualSorting
              sortingHandler={onSortingDidChange}
              showPagination
              showColumnFilters
              showTableHeader={false}
              manualFiltering
              onColumnFiltersChange={onColumnFiltersChange}
              noDataTitleText="No journeys found!"
              onTableReady={onTableReady}
              data-cy="journeys-content"
            />
          </div>
        )}
      </>
    )
  }
)

// MARK: - Custom hooks

const useRequestParams = (
  pageInSearchParams: string | null,
  sizeInSearchParams: string | null,
  sortInSearchParams: string | null
) => {
  const [requestParams, setRequestParams] = useState<Required<GetJourneysUseCaseParams>>({
    ...initialRequestParams,
    page: Math.max(Math.max(0, Number(pageInSearchParams) - 1), initialRequestParams.page),
    size: Number(sizeInSearchParams) || initialRequestParams.size,
    sort: sortInSearchParams?.length ? sortInSearchParams : initialRequestParams.sort,
  })

  return {requestParams, setRequestParams}
}

const useOnPageIndexChange = (
  currentPage: number,
  setRequestParams: React.Dispatch<React.SetStateAction<Required<GetJourneysUseCaseParams>>>,
  setSearchParams: ReturnType<typeof useSearchParams>[1]
) => {
  return useCallback(
    (newPage: number) => {
      // pepsico-ds calls onPageIndexChange with a 1-based page number
      const pageIndex = newPage - 1
      if (pageIndex === currentPage) return
      setRequestParams(prev => ({...prev, page: pageIndex}))
      setSearchParams(oldParams => {
        oldParams.set('page', `${newPage}`)
        return oldParams
      })
    },
    [currentPage, setRequestParams, setSearchParams]
  )
}

const useOnRowsPerPageChange = (
  currentSize: number,
  setRequestParams: React.Dispatch<React.SetStateAction<Required<GetJourneysUseCaseParams>>>,
  setSearchParams: ReturnType<typeof useSearchParams>[1]
) => {
  return useCallback(
    (newSize: number) => {
      if (newSize === currentSize) return
      setRequestParams(prev => ({...prev, size: newSize, page: 0}))
      setSearchParams(oldParams => {
        oldParams.set('size', `${newSize}`)
        oldParams.set('page', '1')
        return oldParams
      })
    },
    [currentSize, setRequestParams, setSearchParams]
  )
}

const useOnSortingDidChange = (
  currentSort: string,
  setRequestParams: React.Dispatch<React.SetStateAction<Required<GetJourneysUseCaseParams>>>,
  setSearchParams: ReturnType<typeof useSearchParams>[1]
) => {
  return useCallback(
    (sorting: {column: string; direction: 'asc' | 'desc'}[]) => {
      const first = sorting[0]
      // TanStack emits [] transiently during setSorting; fall back to default instead of blanking sort
      if (first === undefined) {
        setRequestParams(prev => ({...prev, sort: DEFAULT_SORT}))
        setSearchParams(oldParams => {
          oldParams.set('sort', DEFAULT_SORT)
          return oldParams
        })
        return
      }
      const newSort = first ? `${first.column},${first.direction}` : DEFAULT_SORT
      if (newSort === currentSort) return
      setRequestParams(prev => ({...prev, sort: newSort}))
      setSearchParams(oldParams => {
        oldParams.set('sort', newSort)
        return oldParams
      })
    },
    [currentSort, setRequestParams, setSearchParams]
  )
}

const onLoadJourneysError = () => {
  store.dispatch(
    showNotification({
      show: true,
      type: 'error',
      title: 'Something went wrong!',
      message: 'Please try again later.',
    })
  )
}
